import { Skeleton } from "@/components/ui/feedback/skeleton";
import { cn } from "@/lib/utils";
import { abbreviateNumber, addCommasToNumbers } from "@/lib/utils/prettyName";
import type { ICellRendererParams } from "ag-grid-community";
import { type ReactNode, memo, useMemo } from "react";
import type { ShareVoiceMetricsTableData } from "./useMetricsTable";

type NumberFormat = "abbreviate" | "commas" | "none";

interface MetricsCellRendererProps {
  isLoading?: boolean;
  params: ICellRendererParams<ShareVoiceMetricsTableData>;
  columnTotal?: number;
  prefixRenderer?: ReactNode;
  suffixRenderer?: ReactNode;
  valueClassName?: string;
  numberFormat?: NumberFormat;
}

const MetricsCellRenderer = ({
  isLoading,
  params,
  columnTotal,
  prefixRenderer,
  suffixRenderer,
  valueClassName,
  numberFormat = "none",
}: MetricsCellRendererProps) => {
  const { value } = params;

  const percentage = useMemo(() => {
    if (!columnTotal || columnTotal === 0) return 0;
    return (value / columnTotal) * 100;
  }, [value, columnTotal]);

  const formattedValue = useMemo(() => {
    if (
      params.column?.getColId() === "netSentiment" &&
      typeof value === "number"
    ) {
      return "";
    }

    if (typeof value !== "number") return value;

    switch (numberFormat) {
      case "abbreviate":
        return abbreviateNumber(value);
      case "commas":
        return addCommasToNumbers(value);
      default:
        return value;
    }
  }, [value, numberFormat, params.column]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Skeleton className={"h-4 w-full"} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 min-w-0">
      {prefixRenderer && <div className="shrink-0">{prefixRenderer}</div>}
      <span
        className={cn(
          valueClassName,
          "overflow-hidden text-ellipsis whitespace-nowrap",
        )}
      >
        {formattedValue}
      </span>
      {percentage > 0 && (
        <span
          className={
            "text-gray-600 overflow-hidden text-ellipsis whitespace-nowrap ml-2"
          }
        >
          ({percentage.toFixed(1)}%)
        </span>
      )}
      {suffixRenderer && <div className="shrink-0">{suffixRenderer}</div>}
    </div>
  );
};

MetricsCellRenderer.displayName = "MetricsCellRenderer";

export default memo(MetricsCellRenderer);
